import type { LookResolvedEntity, LookResolveOutcome, LookResolveRequest } from "~/helpers/aiChat/lookResolveContract";

import lookResolveContract from "~/helpers/aiChat/lookResolveContract";
import isRecord from "~/helpers/isRecord";
import { buildEndpoint, executeWithAuthRetry } from "~/services/aiOrchestrator";
import authorizationHeader from "~/services/authorizationHeader";

const readString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

const readMatchSource = (value: unknown): LookResolvedEntity["matchSource"] =>
	value === "barcode" || value === "ocr" || value === "sku" ? value : undefined;

const readClarifyingQuestion = (body: Record<string, unknown>): string | null => {
	const question = "clarifyingQuestion" in body ? readString(body.clarifyingQuestion)?.trim() : undefined;
	return question || null;
};

const readErrorMessages = (body: unknown): string | null => {
	if (!isRecord(body) || !("errors" in body) || !Array.isArray(body.errors)) return null;
	const messages = body.errors
		.map((error: unknown) => (isRecord(error) && "message" in error ? readString(error.message) : undefined))
		.filter((message): message is string => !!message);
	return messages.length ? messages.join("; ") : null;
};

const toResolvedEntity = (raw: unknown): LookResolvedEntity | null => {
	if (!isRecord(raw)) return null;
	const entityId = "entityId" in raw ? readString(raw.entityId) : undefined;
	const entityType = "entityType" in raw ? raw.entityType : undefined;
	if (!entityId || !lookResolveContract.isLookEntityType(entityType)) return null;
	return {
		displayName: ("displayName" in raw ? readString(raw.displayName) : undefined) ?? "",
		entityId,
		entityType,
		matchSource: "matchSource" in raw ? readMatchSource(raw.matchSource) : undefined
	};
};

const parseOutcome = (payload: unknown): LookResolveOutcome => {
	if (!isRecord(payload) || !("kind" in payload)) throw new Error("Look resolve returned no outcome");
	if (payload.kind === "resolved") {
		const entity = toResolvedEntity("entity" in payload ? payload.entity : null);
		if (!entity) throw new Error("Look resolve returned a resolved result without a usable entity");
		const extractedText = "extractedText" in payload ? readString(payload.extractedText) : undefined;
		return { entity, extractedText, kind: "resolved" };
	}
	if (payload.kind === "ambiguous") {
		const rawCandidates = "candidates" in payload && Array.isArray(payload.candidates) ? payload.candidates : [];
		const candidates = rawCandidates
			.map(toResolvedEntity)
			.filter((entity): entity is LookResolvedEntity => entity !== null);
		return { candidates, clarifyingQuestion: readClarifyingQuestion(payload), kind: "ambiguous" };
	}
	if (payload.kind === "unresolved") return { clarifyingQuestion: readClarifyingQuestion(payload), kind: "unresolved" };
	throw new Error("Look resolve returned an unrecognized outcome");
};

const readJson = async (response: Response): Promise<unknown> => {
	try {
		return await response.json();
	} catch {
		return null;
	}
};

const postLookResolve = async (token: string, request: LookResolveRequest): Promise<LookResolveOutcome> => {
	const response = await fetch(lookResolveContract.resolveLookEndpoint(buildEndpoint()), {
		body: JSON.stringify(request),
		headers: {
			Authorization: authorizationHeader(token),
			"Content-Type": "application/json"
		},
		method: "POST"
	});
	const body = await readJson(response);

	if (!response.ok) {
		throw new lookResolveContract.LookResolveHttpError(response.status, readErrorMessages(body) ?? response.statusText);
	}

	const envelopeErrors = readErrorMessages(body);
	if (envelopeErrors) throw new Error(envelopeErrors);
	return parseOutcome(isRecord(body) && "result" in body ? body.result : body);
};

const resolveLookFrame = (request: LookResolveRequest): Promise<LookResolveOutcome> =>
	executeWithAuthRetry("query", (token) => postLookResolve(token, request));

export default resolveLookFrame;
